import React, { useState } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { adminLifecycleService } from "../../../../services/api/settings";
import {
  LifecyclePhase,
  LifecyclePepmTier,
  LifecycleVolumeDiscount,
} from "../../../../store/types/settings";
import formatPrice0 from "../../../../utility/formatPrice0";
import EditLifecyclePhaseModal from "./EditLifecyclePhaseModal";
import EditLifecycleTierModal from "./EditLifecycleTierModal";
import EditLifecycleDiscountModal from "./EditLifecycleDiscountModal";

interface LifecycleTablesProps {
  data: {
    phases: LifecyclePhase[];
    pepmTiers: LifecyclePepmTier[];
    volumeDiscounts: LifecycleVolumeDiscount[];
  };
  refresh: () => void;
}

const LifecycleTables: React.FC<LifecycleTablesProps> = ({ data, refresh }) => {
  const [deleting, setDeleting] = useState<string | null>(null);

  const [phaseModal, setPhaseModal] = useState<{
    isOpen: boolean;
    data: LifecyclePhase | null;
  }>({ isOpen: false, data: null });
  const [tierModal, setTierModal] = useState<{
    isOpen: boolean;
    data: LifecyclePepmTier | null;
  }>({ isOpen: false, data: null });
  const [discountModal, setDiscountModal] = useState<{
    isOpen: boolean;
    data: LifecycleVolumeDiscount | null;
  }>({ isOpen: false, data: null });

  const handleDeletePhase = async (id: number) => {
    if (!window.confirm("Delete this phase?")) return;
    setDeleting(`phase-${id}`);
    await adminLifecycleService.phases.delete(id);
    refresh();
    setDeleting(null);
  };

  const handleDeleteTier = async (id: number) => {
    if (!window.confirm("Delete this PEPM tier?")) return;
    setDeleting(`tier-${id}`);
    await adminLifecycleService.pepmTiers.delete(id);
    refresh();
    setDeleting(null);
  };

  const handleDeleteDiscount = async (id: number) => {
    if (!window.confirm("Delete this volume discount?")) return;
    setDeleting(`discount-${id}`);
    await adminLifecycleService.volumeDiscounts.delete(id);
    refresh();
    setDeleting(null);
  };

  return (
    <div className="space-y-8">
      {/* Phases */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Lifecycle Phases</h2>
          <button
            onClick={() => setPhaseModal({ isOpen: true, data: null })}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all font-medium text-sm"
          >
            <Plus size={16} /> Add Phase
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Mandatory
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Setup Fee
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Monthly
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.phases?.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-6 py-3">
                    <div className="font-semibold text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500 truncate max-w-xs">
                      {p.description}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${p.isMandatory ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-600"}`}
                    >
                      {p.isMandatory ? "Yes" : "No"}
                    </span>
                  </td>
                  {/* * 100, "USD" */}
                  <td className="px-6 py-3 font-medium">
                    {formatPrice0(p.setupFee, "NGN")}
                  </td>
                  <td className="px-6 py-3 font-medium">
                    {formatPrice0(p.monthlySubscription, "NGN")}
                  </td>
                  <td className="px-6 py-3 text-right space-x-2">
                    <button
                      onClick={() => setPhaseModal({ isOpen: true, data: p })}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeletePhase(p.id)}
                      disabled={deleting === `phase-${p.id}`}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {(!data.phases || data.phases.length === 0) && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-4 text-center text-gray-500 text-sm"
                  >
                    No phases found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PEPM Tiers */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">PEPM Tiers</h2>
          <button
            onClick={() => setTierModal({ isOpen: true, data: null })}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all font-medium text-sm"
          >
            <Plus size={16} /> Add Tier
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Employee Range
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.pepmTiers?.map((t) => (
                <tr
                  key={t.id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-6 py-3 font-semibold text-gray-900">
                    {t.isCustom
                      ? "Custom"
                      : `${t.minEmployees.toLocaleString()} - ${t.maxEmployees ? t.maxEmployees.toLocaleString() : "+"}`}
                  </td>
                  <td className="px-6 py-3 font-medium">
                    {t.isCustom
                      ? "Custom Pricing"
                      : formatPrice0(t.pepmRate, "NGN")}
                  </td>
                  <td className="px-6 py-3 text-right space-x-2">
                    <button
                      onClick={() => setTierModal({ isOpen: true, data: t })}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteTier(t.id)}
                      disabled={deleting === `tier-${t.id}`}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {(!data.pepmTiers || data.pepmTiers.length === 0) && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-4 text-center text-gray-500 text-sm"
                  >
                    No PEPM tiers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Volume Discounts */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Volume Discounts</h2>
          <button
            onClick={() => setDiscountModal({ isOpen: true, data: null })}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all font-medium text-sm"
          >
            <Plus size={16} /> Add Discount
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Label
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Phases Count
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.volumeDiscounts?.map((d) => (
                <tr
                  key={d.id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-6 py-3 font-semibold text-gray-900">
                    {d.label}
                  </td>
                  <td className="px-6 py-3 text-gray-600">
                    {d.allPhases
                      ? "All Phases"
                      : `${d.minAdditionalPhases} - ${d.maxAdditionalPhases}`}
                  </td>
                  <td className="px-6 py-3 font-medium text-emerald-600">
                    {d.discountPct}%
                  </td>
                  <td className="px-6 py-3 text-right space-x-2">
                    <button
                      onClick={() =>
                        setDiscountModal({ isOpen: true, data: d })
                      }
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteDiscount(d.id)}
                      disabled={deleting === `discount-${d.id}`}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {(!data.volumeDiscounts || data.volumeDiscounts.length === 0) && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-4 text-center text-gray-500 text-sm"
                  >
                    No volume discounts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EditLifecyclePhaseModal
        isOpen={phaseModal.isOpen}
        onClose={() => setPhaseModal({ isOpen: false, data: null })}
        data={phaseModal.data}
        onSuccess={refresh}
      />

      <EditLifecycleTierModal
        isOpen={tierModal.isOpen}
        onClose={() => setTierModal({ isOpen: false, data: null })}
        data={tierModal.data}
        onSuccess={refresh}
      />

      <EditLifecycleDiscountModal
        isOpen={discountModal.isOpen}
        onClose={() => setDiscountModal({ isOpen: false, data: null })}
        data={discountModal.data}
        onSuccess={refresh}
      />
    </div>
  );
};

export default LifecycleTables;
